// Assessments screen (/$region/assessments, Confidential): the header with "Upload document" and "New assessment", and the Assessments and Capability documents tabs (the tab and the status filter in the URL) with their counts; uploading switches to the documents, where the new document's reading is followed live.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileUpIcon, PlusIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { regionParam, useRegion } from "@/app/region";
import { PageHeader } from "@/components/common/PageHeader";
import { ReadOnlyGuard } from "@/components/common/ReadOnlyGuard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useActionAvailability } from "@/app/session";
import { formatNumber } from "@/lib/format";

import { AssessmentsTable } from "./AssessmentsTable";
import { CapabilityDocumentsTable } from "./CapabilityDocumentsTable";
import type { AssessmentStage } from "./labels";
import { assessmentsQueryOptions, capabilityDocumentsQueryOptions } from "./queries";
import { UploadDocumentDialog } from "./UploadDocumentDialog";

export const ASSESSMENTS_TABS = ["assessments", "documents"] as const;
export type AssessmentsTab = (typeof ASSESSMENTS_TABS)[number];

export interface AssessmentsSearch {
  tab: AssessmentsTab;
  status?: AssessmentStage[] | undefined;
}

export const ASSESSMENTS_LEAD =
  "Add a document to compare with the grid code, then review each topic and sign it off.";

interface AssessmentsPageProps {
  search: AssessmentsSearch;
  onSearchChange: (patch: Partial<AssessmentsSearch>, replace?: boolean) => void;
}

function TabLabel({ label, count }: { label: string; count: number | undefined }): ReactNode {
  return (
    <>
      {label}
      {count === undefined ? null : (
        <span className="rounded-pill bg-muted px-1.5 text-label/[18px] font-semibold text-ink-2 tabular-nums">
          {formatNumber(count)}
        </span>
      )}
    </>
  );
}

export function AssessmentsPage({ search, onSearchChange }: AssessmentsPageProps) {
  const { jurisdiction } = useRegion();
  const region = regionParam(jurisdiction);
  const writable = useActionAvailability(false);
  const [uploading, setUploading] = useState(false);
  const assessments = useQuery(assessmentsQueryOptions(jurisdiction.jurisdiction_id));
  const documents = useQuery(capabilityDocumentsQueryOptions());
  const upload = () => {
    setUploading(true);
  };
  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in px-4 pt-6 pb-16 md:px-7">
      <PageHeader
        eyebrow="Compliance"
        title="Assessments"
        lead={ASSESSMENTS_LEAD}
        actions={
          <>
            <ReadOnlyGuard>
              {(availability) => (
                <Button variant="outline" disabled={availability.disabled} onClick={upload}>
                  <FileUpIcon aria-hidden />
                  Add document
                </Button>
              )}
            </ReadOnlyGuard>
            <Button asChild>
              <Link to="/$region/assessments/new" params={{ region }}>
                <PlusIcon aria-hidden />
                New assessment
              </Link>
            </Button>
          </>
        }
      />
      <Tabs
        className="mt-5 gap-3.5"
        value={search.tab}
        onValueChange={(value) => {
          onSearchChange({ tab: value as AssessmentsTab });
        }}
      >
        <TabsList variant="underline" aria-label="Assessments views" className="w-full border-b">
          <TabsTrigger value="assessments">
            <TabLabel label="Assessments" count={assessments.data?.length} />
          </TabsTrigger>
          <TabsTrigger value="documents">
            <TabLabel label="Documents" count={documents.data?.length} />
          </TabsTrigger>
        </TabsList>
        <TabsContent value="assessments">
          <AssessmentsTable
            region={region}
            jurisdiction={jurisdiction.jurisdiction_id}
            stages={search.status ?? []}
            onStagesChange={(stages) => {
              onSearchChange({ status: stages.length === 0 ? undefined : stages }, true);
            }}
            onUpload={upload}
            uploadDisabled={writable.disabled}
          />
        </TabsContent>
        <TabsContent value="documents">
          <CapabilityDocumentsTable region={region} onUpload={upload} uploadDisabled={writable.disabled} />
        </TabsContent>
      </Tabs>
      <UploadDocumentDialog
        open={uploading}
        onOpenChange={setUploading}
        region={region}
        onUploaded={() => {
          onSearchChange({ tab: "documents" }, true);
        }}
      />
    </div>
  );
}
